import { EditFilled, EyeOutlined } from '@ant-design/icons';
import { Space, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import useDebounce from '../../../../../hooks/useDebounce';
import { MarketModel } from '../../../../../models/market';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import {
  paginateMarkets,
  searchMarketsByName,
} from '../../../../../store/features/metaData/metaDataSlice';
import { DrawerHashRoute } from '../../../../containers/Drawers/types';
import NoResultsFound from '../../../../elements/NoResultsFound';
import { RoutePath } from '../../../AppRoot/types';

import { SortOrder } from 'antd/es/table/interface';

import { useAuth } from '../../../../../hooks/useAuth';
import { ProductStatusEnum } from '../../../../../models/product-library';
import { UserPermissions } from '../../../../../util';
import SearchBar from '../../../../elements/SearchBar/SearchBar';

const { Text } = Typography;

const MarketsTab = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounce<string>(searchQuery, 500);
  const { hasPermission } = useAuth();

  const { allMarkets, fetchingMarkets, searchingMarkets, count, current } =
    useAppSelector((state) => state.metaData);

  const hasNoSearchResultsFound = useMemo(
    () => !fetchingMarkets && !!searchQuery && allMarkets.length === 0,
    [fetchingMarkets, searchQuery, allMarkets.length]
  );

  const hasMarkets = !(!fetchingMarkets && allMarkets.length === 0);

  const columns: ColumnsType<MarketModel> = useMemo(
    () =>
      getColumns(history, hasPermission).map((column) => ({
        ...column,
        defaultSortOrder: column.defaultSortOrder as SortOrder | undefined,
      })),
    [history, hasPermission]
  );

  useEffect(() => {
    dispatch(
      searchMarketsByName({
        query: debouncedSearchQuery,
      })
    );
  }, [debouncedSearchQuery, dispatch]);

  const updateSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const paginate = useCallback(
    (page: number, pageSize: number) => {
      dispatch(
        paginateMarkets({
          page,
          pageSize,
          queryStr: debouncedSearchQuery,
        })
      );
    },
    [debouncedSearchQuery, dispatch]
  );

  const paginationSetting = {
    hideOnSinglePage: false,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    onChange: paginate,
    onShowSizeChange: paginate,
    showTotal: (total: number, range: number[]) =>
      `${range[0]}-${range[1]} of ${total} items`,
    style: {
      marginTop: '0px',
      padding: '2rem 1rem',
      background: 'white',
      borderRadius: '0 0 4px 4px',
    },
    total: count,
    current,
  };

  return (
    <div>
      <SearchBar
        searchQuery={searchQuery}
        updateSearchQuery={updateSearchQuery}
        isSearching={searchingMarkets}
        canCreate={hasPermission(UserPermissions.MetaDataCreate)}
        createLink={DrawerHashRoute.MarketForm}
        createButtonText="Add New Market"
        placeholder="Search Markets"
      />
      {hasMarkets && hasNoSearchResultsFound ? (
        <NoResultsFound
          searchQuery={searchQuery}
          onClear={() => updateSearchQuery('')}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={allMarkets}
          sortDirections={['ascend', 'descend', 'ascend']}
          pagination={paginationSetting}
          loading={searchingMarkets}
        />
      )}
    </div>
  );
};

const getColumns = (
  history: any,
  hasPermission: (permission: UserPermissions | UserPermissions[]) => boolean
) => [
  {
    title: 'Market',
    dataIndex: 'name',
    width: '35%',
    sorter: (a: MarketModel, b: MarketModel) => (a.name > b.name ? 1 : -1),
  },
  {
    title: '# in Processing',
    dataIndex: 'in_processing',
    defaultSortOrder: 'descend',
    sorter: (a: MarketModel, b: MarketModel) =>
      (a.in_processing || 0) - (b.in_processing || 0),
  },
  {
    title: '# in Library',
    dataIndex: 'in_library',
    defaultSortOrder: 'descend',
    sorter: (a: MarketModel, b: MarketModel) =>
      (a.in_library || 0) - (b.in_library || 0),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    defaultSortOrder: 'descend',
    sorter: (a: MarketModel, b: MarketModel) =>
      a.status && b.status ? (a.status > b.status ? 1 : -1) : 0,
    render: (status: ProductStatusEnum) => (
      <Text
        type={
          status === ProductStatusEnum.needs_approval ? 'danger' : undefined
        }
      >
        {status === ProductStatusEnum.approved ? status : 'Needs Approval'}
      </Text>
    ),
  },
  {
    title: 'Actions',
    width: 100,
    align: 'right' as const,
    render: (market: MarketModel) => (
      <Space direction="horizontal" size="middle">
        {hasPermission(UserPermissions.MetaDataEdit) && (
          <Tooltip title="Edit">
            <EditFilled
              onClick={() =>
                history.push({
                  hash: DrawerHashRoute.MarketForm,
                  state: { data: market },
                })
              }
              className="metadata-table-action-icon"
            />
          </Tooltip>
        )}
        <Tooltip title="View in Library">
          <EyeOutlined
            onClick={() =>
              history.push({
                state: { market },
                pathname: RoutePath.ProductLibrary,
              })
            }
            className="metadata-table-action-icon"
          />
        </Tooltip>
      </Space>
    ),
  },
];

export default MarketsTab;
